<template>
  <div class="action-menu-item" @click="exportDocx">
    Generate report
  </div>
</template>

<script>
import { DATE_FORMAT } from '@/main.config.js'
import { eventsConfig, notShow } from '@/events.config.js'

import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts.js'
import { patientMaxHR } from '@/mixins/patient-max-hr'
import { fullName } from '@/mixins/full-name.js'
import timeString from '@/modules/time-string.js'
import { trainingDate } from '@/mixins/training-date.js'
import getAverageOfArray from '@/modules/average-of-array.js'
import { userRights } from '@/mixins/user-rights.js'

export default {
  name: 'ProtocolSessionEn',
  mixins: [patientMaxHR, fullName, trainingDate, userRights],
  props: {
    training: {
      type: Object,
      required: true
    },
    patient: {
      type: Object,
      required: true
    },
    stepRate: {
      type: Object,
      required: false
    },
    rhythmogram: {
      type: Object,
      required: false
    },
    events: {
      type: Array,
      required: false
    }
  },
  data: () => ({
    eventsList: null
  }),
  computed: {
    fullNamePatient() {
      return this.getFullName(this.patient)
    },
    gender() {
      if (this.patient.gender === 0) {
        return 'Male'
      } else if (this.patient.gender === 1) {
        return 'Female'
      } else return '—'
    },
    height() {
      if (this.patient.height) return `${this.patient.height} cm`
      else return '—'
    },
    weight() {
      if (this.patient.weight) return `${this.patient.weight} kg`
      else return '—'
    },
    datePlan() {
      return this.moment(this.training.date_plan).format(DATE_FORMAT.en)
    },
    intervalHR() {
      if (this.training.heart_rate_min && this.training.heart_rate_max) {
        return `${this.training.heart_rate_min} - ${this.training.heart_rate_max} bpm`
      } else return '—'
    },
    startTime() {
      let time = this.moment.utc(this.training.date_start, DATE_FORMAT.serverFull).local()
      return time.format(DATE_FORMAT.timeHMS)
    },
    endTime() {
      let time = this.moment.utc(this.training.date_end, DATE_FORMAT.serverFull).local()
      return time.format(DATE_FORMAT.timeHMS)
    },
    adjustedDurationStr() {
      return !this.training.duration || this.training.duration === 0 ? '—' : `${this.training.duration} min`
    },
    duration() {
      return this.getDoneTrainingDuration(this.training)
    },
    diagnosisStr() {
      if (this.training.diagnosis) return this.training.diagnosis
      else return '–'
    },
    trainingDurationString() {
      if (this.duration === 0) {
        return '–'
      }
      return timeString(this.duration)
    },
    hasStepRate() {
      return (
        !!this.stepRate &&
        typeof this.stepRate.rateValues !== 'undefined' &&
        this.stepRate.rateValues.length > 0
      )
    },
    averageStepRate() {
      if (!this.hasStepRate) {
        return '–'
      }
      return `${getAverageOfArray(this.stepRate.rateValues)} step/min`
    },
    stepCountStr() {
      if (!this.training.step_count) {
        return '-'
      } else return this.training.step_count
    },
    borgScaleStr() {
      if (!this.training.borg_scale) {
        return '-'
      } else return this.training.borg_scale
    },
    hasRhythmogram() {
      return (
        !!this.rhythmogram &&
        typeof this.rhythmogram.hrValues !== 'undefined' &&
        this.rhythmogram.hrValues.length > 0
      )
    },
    maxHeartRate() {
      if (!this.hasRhythmogram) {
        return '–'
      }
      return Math.max(...this.rhythmogram.hrValues)
    },
    averageHeartRate() {
      if (!this.hasRhythmogram) {
        return '–'
      }
      return getAverageOfArray(this.rhythmogram.hrValues)
    },
    hrInZonePercent() {
      if (!this.hasRhythmogram) {
        return '–'
      }
      if (!this.training.heart_rate_max || !this.training.heart_rate_min) {
        return 100
      }
      const fullCount = this.rhythmogram.hrValues.length
      const zoneCount = this.rhythmogram.hrValues.reduce((count, value) => {
        if (value <= this.training.heart_rate_max && value >= this.training.heart_rate_min) {
          count++
        }
        return count
      }, 0)
      return Math.round((zoneCount / fullCount) * 100)
    },
    patientCommentStr() {
      let text = this.events.filter(ev => ev.type === 'eventText')
      let strResult = ''
      text.forEach(ev => {
        let timeBegin = timeString(ev.begin, true)
        strResult += `${timeBegin} - ${ev.value}\n`
      })
      if (strResult !== '') strResult += '\n'
      if (this.training.comment) {
        strResult += `${this.training.comment}`
      }
      if (strResult !== '') return strResult
      else return 'No complaints'
    },
    basicRhythm() {
      if (this.training.basic_rhythm) return this.training.basic_rhythm
      else return '–'
    },
    exercise() {
      return this.training.description ? this.training.description : '-'
    },
    doctorCommentStr() {
      if (this.training.specialist_interpretation) return this.training.specialist_interpretation
      else return '–'
    },
    conclusionStr() {
      let str = ''
      if (this.training.basic_rhythm) str += `basic rhythm ${this.basicRhythm}, `
      if (this.hasRhythmogram) str += `average Heart Rate ${this.averageHeartRate} bpm, `
      if (this.hasRhythmogram) str += `maximum Heart Rate ${this.maxHeartRate} bpm, `
      if (str !== '') {
        str = str.replace(/,(?=[^,]+$)/, '.')
      }
      if (this.training.specialist_interpretation)
        str += `\nInterpretation by a specialist: ${this.doctorCommentStr}.`
      return str
    },
    tableForRegistredEvents() {
      let firstColumn = [
        [{ text: 'Event', style: 'tableHeader' }, { text: 'Number of', style: 'tableHeader' }]
      ]
      let secondColumn = [
        [{ text: 'Event', style: 'tableHeader' }, { text: 'Number of', style: 'tableHeader' }]
      ]
      if (this.eventsList.length >= 10) {
        let halfLengthEventsList = Math.round(this.eventsList.length / 2)
        for (let i = 0; i < halfLengthEventsList; i++) {
          let row = new Array()
          row.push({ text: `${this.eventsList[i].text}` })
          row.push({ text: `${this.eventsList[i].count}`, alignment: 'center' })
          firstColumn.push(row)
        }
        for (let i = halfLengthEventsList; i < this.eventsList.length; i++) {
          let row = new Array()
          row.push({ text: `${this.eventsList[i].text}` })
          row.push({ text: `${this.eventsList[i].count}`, alignment: 'center' })
          secondColumn.push(row)
        }
      } else {
        this.eventsList.forEach(el => {
          let row = new Array()
          row.push({ text: `${el.text}` })
          row.push({ text: `${el.count}`, alignment: 'center' })
          firstColumn.push(row)
        })
      }
      return { firstColumn, secondColumn }
    }
  },
  methods: {
    async exportDocx() {
      this.addEventsToList()
      pdfMake.vfs = pdfFonts.pdfMake.vfs
      const logoNSEng = `<svg xmlns="http://www.w3.org/2000/svg" width="88" height="20" viewBox="0 0 88 20" fill="none">
                          <g clip-path="url(#clip0_8_2)">
                            <path d="M9.06216 13.6049C8.13961 13.6049 7.39362 12.8357 7.39362 11.8844C7.39362 10.9331 8.13961 10.1639 9.06216 10.1639C9.98472 10.1639 10.7307 10.9331 10.7307 11.8844C10.7307 12.8357 9.98472 13.6049 9.06216 13.6049ZM18.0317 11.7433C18.0317 11.7433 17.4269 11.4884 17.2371 11.3792C15.2596 10.2868 14.1561 8.51616 14.1737 6.4224C14.1737 6.35412 14.1737 6.28585 14.1737 6.21757C14.1737 6.1493 14.1781 6.08102 14.1825 6.01275C14.1825 5.94447 14.1914 5.8762 14.1958 5.80337C14.2002 5.73509 14.209 5.66227 14.2134 5.59399C14.2223 5.52572 14.2267 5.45744 14.2355 5.38462C14.2443 5.31634 14.2532 5.24352 14.262 5.17524C14.2708 5.10697 14.2841 5.03869 14.2929 4.96586C14.3017 4.89759 14.315 4.82931 14.3282 4.76104C14.3415 4.69276 14.3503 4.62449 14.3679 4.55621C14.3812 4.48794 14.3944 4.41966 14.4077 4.35139C14.4209 4.28311 14.4342 4.21484 14.4518 4.14656C14.4651 4.07829 14.4827 4.01457 14.496 3.94629C14.5136 3.87802 14.5268 3.81429 14.5445 3.74602C14.5622 3.6823 14.5798 3.61402 14.5931 3.5503C14.6107 3.48657 14.6284 3.42285 14.646 3.35458C14.6637 3.29085 14.6813 3.22713 14.699 3.16341C14.7167 3.09968 14.7343 3.04051 14.752 2.97679C14.7696 2.91762 14.7873 2.85389 14.8049 2.79472C14.8226 2.73555 14.8403 2.67638 14.8623 2.61721C14.88 2.55803 14.902 2.49886 14.9197 2.43969C14.9374 2.38052 14.9594 2.3259 14.9771 2.27128C14.9947 2.21666 15.0168 2.16204 15.0345 2.10742C15.0521 2.0528 15.0698 2.00273 15.0919 1.94811C15.1095 1.89804 15.1272 1.84342 15.1492 1.79336C15.1669 1.74329 15.1846 1.69322 15.2022 1.64315C15.2199 1.59308 15.2375 1.54757 15.2552 1.50205C15.2728 1.45653 15.2905 1.41102 15.3081 1.3655C15.3258 1.31998 15.3435 1.27902 15.3611 1.23805C15.3788 1.19709 15.392 1.15612 15.4097 1.11516C15.4273 1.07874 15.4406 1.03778 15.4582 1.00137C15.4715 0.964953 15.4891 0.92854 15.5024 0.892127C15.5156 0.860265 15.5289 0.828403 15.5421 0.79199C15.5553 0.760128 15.5686 0.732818 15.5818 0.700957C15.5951 0.673647 15.6039 0.646337 15.6171 0.619027C15.626 0.596269 15.6392 0.568959 15.648 0.5462C15.6569 0.523442 15.6657 0.505235 15.6745 0.482477C15.6833 0.46427 15.6922 0.446064 15.6966 0.427857C15.7231 0.368686 15.754 0.304962 15.7716 0.241239V0.227584C15.7893 0.182067 15.7849 0.127447 15.7584 0.0864825C15.7363 0.0500693 15.6966 0.0182076 15.6569 0.0091043C15.6127 -0.00455069 15.5642 9.75734e-07 15.5289 0.0273109C15.4891 0.0500693 15.4626 0.0910342 15.4317 0.127448C15.4008 0.168412 15.3744 0.209377 15.3479 0.250342C15.317 0.300411 15.2817 0.345927 15.2508 0.395996C15.2066 0.459719 15.1625 0.523442 15.1139 0.591717C15.0565 0.669095 14.9992 0.751025 14.9418 0.828403C14.8756 0.919437 14.8049 1.01047 14.7387 1.1015C14.6593 1.20164 14.5842 1.30633 14.5048 1.40646C14.4165 1.5157 14.3282 1.62494 14.2399 1.73418C14.1428 1.84798 14.0457 1.96632 13.9486 2.08011C13.8427 2.19845 13.7367 2.3168 13.6308 2.43514C13.5204 2.55803 13.4057 2.67638 13.2909 2.79472C13.1717 2.91762 13.0525 3.03596 12.9289 3.1543C12.8053 3.27265 12.6773 3.39099 12.5493 3.50478C12.4169 3.61857 12.2889 3.73236 12.1521 3.8416C12.0152 3.95084 11.8784 4.05553 11.7415 4.16022C11.6091 4.2558 11.4723 4.35139 11.331 4.44242C10.1348 5.21165 9.35349 5.29358 9.04892 5.32545C7.2612 5.52572 5.78688 4.59718 5.39402 4.34229C5.08945 4.14656 4.85109 3.95084 4.69218 3.80974C4.58624 3.72781 4.43616 3.81885 4.45382 3.95084C4.45382 3.95084 4.60831 5.21621 4.63038 5.63951C4.68335 6.55439 4.63921 7.66955 3.97709 8.85298C3.2002 10.2503 1.91569 10.9012 1.59346 11.0605C1.16088 11.279 0.0838318 11.7478 0.0838318 11.7478C-0.0353495 11.8025 -0.0353495 11.98 0.0838318 12.0346C0.0838318 12.0346 1.22268 12.5353 1.59346 12.7219C2.48953 13.168 3.30173 13.7324 3.96826 14.9158C4.74956 16.3086 4.63038 17.7196 4.63038 18.1429C4.63038 18.6391 4.45382 19.8316 4.45382 19.8316C4.44057 19.9681 4.58624 20.0546 4.69218 19.9727C4.69218 19.9727 5.68094 19.208 6.02083 18.9668C6.7315 18.4706 7.71585 17.9472 9.03126 17.9426C10.6027 17.9335 11.7989 18.7574 12.0903 18.9668C12.4257 19.2126 13.4189 19.9727 13.4189 19.9727C13.5248 20.0546 13.6705 19.9636 13.6573 19.8316C13.6573 19.8316 13.4984 18.5662 13.4807 18.1429C13.4366 17.1416 13.4719 16.1083 14.1428 14.9158C14.9241 13.5275 16.1998 12.8812 16.5176 12.7219C16.9458 12.5034 18.0273 12.0346 18.0273 12.0346C18.1464 11.98 18.1464 11.8025 18.0273 11.7478L18.0317 11.7433Z" fill="#0AADF9"/>
                            <path d="M73.3363 15.4028C73.9322 15.4028 74.378 15.1934 74.6693 14.7792C74.9651 14.365 75.1107 13.8279 75.1107 13.1771C75.1107 12.5262 74.9607 11.9618 74.6649 11.5567C74.3647 11.1516 73.9233 10.9513 73.3363 10.9513C72.9302 10.9513 72.5991 11.0469 72.3387 11.2335C72.0782 11.4201 71.8884 11.6841 71.7604 12.0164C71.6368 12.3487 71.5706 12.7355 71.5706 13.1771C71.5706 13.8553 71.7207 14.3969 72.0164 14.7974C72.3122 15.198 72.7536 15.3983 73.3318 15.3983L73.3363 15.4028ZM48.5774 16.9914V9.36732H50.2592V10.6236C50.2857 10.5781 50.3387 10.4688 50.4711 10.2913C50.6344 10.0683 50.8375 9.88621 51.0758 9.74055C51.2568 9.62676 51.4554 9.53573 51.6717 9.47201C51.888 9.40828 52.1087 9.36732 52.3383 9.34911C52.5678 9.33091 52.7973 9.34001 53.0269 9.36732V11.2016C52.815 11.1334 52.5722 11.1106 52.2941 11.1334C52.016 11.1561 51.7644 11.2198 51.5393 11.3336C51.3142 11.4383 51.1244 11.5794 50.9699 11.7569C50.8154 11.9345 50.7006 12.1393 50.6212 12.3805C50.5417 12.6172 50.502 12.8858 50.502 13.1862V16.9959H48.5819L48.5774 16.9914ZM77.7725 9.36732H78.9643C78.9643 9.22166 78.9687 9.06236 78.9775 8.88939C78.9863 8.6436 79.0261 8.39781 79.0923 8.14747C79.1585 7.89713 79.2865 7.66955 79.4675 7.46928C79.7014 7.20528 79.953 7.02321 80.2267 6.92763C80.5004 6.83204 80.7652 6.77742 81.0213 6.77287C81.2773 6.76377 81.498 6.76377 81.679 6.76377H82.6192V8.32044H81.7496C81.4494 8.32044 81.2287 8.39781 81.0831 8.55257C80.9374 8.70733 80.8668 8.88939 80.8668 9.10332V9.37642H82.6192V10.8921H80.8668V16.9959H78.9599V10.8921H77.768V9.37642L77.7725 9.36732ZM45.2183 16.9686V16.1629C45.0373 16.3678 44.8343 16.5498 44.6136 16.7C44.1236 17.0323 43.51 17.2007 42.7684 17.2007C42.177 17.2007 41.6914 17.096 41.3162 16.8867C40.941 16.6773 40.6497 16.4133 40.4422 16.0947C40.2347 15.7761 40.0847 15.4438 39.9964 15.0979C39.9081 14.7519 39.8551 14.4379 39.8331 14.1511C39.811 13.8644 39.8066 13.655 39.8066 13.5275V9.36277H41.74V12.8266C41.74 13.0041 41.7488 13.2362 41.7664 13.5139C41.7841 13.7915 41.8415 14.0737 41.943 14.3514C42.0401 14.6336 42.2034 14.8657 42.4241 15.0569C42.6449 15.2435 42.9583 15.3391 43.3688 15.3391C43.5321 15.3391 43.7087 15.3118 43.8941 15.2572C44.0794 15.2025 44.256 15.1024 44.4193 14.9477C44.5297 14.843 44.6312 14.7064 44.7151 14.5335C44.8872 14.1739 44.9755 13.6914 44.9799 13.0997V9.35821H46.9001V16.964H45.2183V16.9686ZM88 9.36732H85.815V6.75922H83.9081V13.7733C83.9081 14.233 83.9125 14.6472 83.9213 15.0114C83.9302 15.3755 84.0273 15.7351 84.2171 16.0856C84.4245 16.4634 84.7203 16.7319 85.1176 16.9003C85.5104 17.0687 85.9607 17.1598 86.4639 17.1734C86.9671 17.1871 87.4791 17.1416 88 17.0414V15.4301C87.5497 15.4939 87.1436 15.5121 86.7729 15.4848C86.4021 15.462 86.1372 15.3118 85.9651 15.0432C85.8768 14.9021 85.8282 14.711 85.8194 14.4697C85.8106 14.2285 85.8106 13.9599 85.8106 13.6595V10.8876H87.9956V9.37187L88 9.36732ZM57.3218 15.4028C57.9177 15.4028 58.3636 15.1934 58.6549 14.7792C58.9506 14.365 59.0963 13.8279 59.0963 13.1771C59.0963 12.5262 58.9462 11.9618 58.6505 11.5567C58.3547 11.1516 57.9089 10.9513 57.3218 10.9513C56.9157 10.9513 56.5847 11.0469 56.3242 11.2335C56.0638 11.4201 55.874 11.6841 55.746 12.0164C55.6224 12.3487 55.5562 12.7355 55.5562 13.1771C55.5562 13.8553 55.7063 14.3969 56.002 14.7974C56.2977 15.198 56.7392 15.3983 57.3174 15.3983L57.3218 15.4028ZM57.3218 17.1916C56.5802 17.1916 55.9314 17.0187 55.3708 16.6773C54.8102 16.3359 54.3732 15.8625 54.0642 15.2572C53.7508 14.6518 53.5963 13.9599 53.5963 13.1771C53.5963 12.3942 53.7552 11.6841 54.073 11.0833C54.3908 10.4779 54.8323 10.0091 55.3884 9.67228C55.949 9.33546 56.5935 9.16704 57.3218 9.16704C58.0501 9.16704 58.7167 9.34001 59.2773 9.68138C59.8379 10.0228 60.2793 10.4961 60.5927 11.1015C60.9061 11.7023 61.065 12.3987 61.065 13.1816C61.065 13.9645 60.9061 14.6655 60.5927 15.2708C60.2749 15.8716 59.8379 16.345 59.2729 16.6864C58.7123 17.0278 58.0634 17.1962 57.3262 17.1962L57.3218 17.1916ZM35.0128 10.7283C34.7215 10.7283 34.4434 10.7783 34.1697 10.8739C33.9005 10.9695 33.6842 11.1425 33.5253 11.3883C33.3664 11.634 33.2781 11.9618 33.2604 12.3669H36.7299C36.7211 11.9299 36.6372 11.5976 36.4827 11.3564C36.3282 11.1197 36.1252 10.9558 35.8736 10.8648C35.622 10.7738 35.3351 10.7283 35.0172 10.7283H35.0128ZM35.0835 17.2007C34.4081 17.2007 33.7901 17.0596 33.2207 16.7729C32.6557 16.4861 32.201 16.0537 31.8656 15.4665C31.5301 14.8794 31.3624 14.142 31.3624 13.2544V12.9631C31.3624 12.112 31.5213 11.4065 31.8479 10.8421C32.1701 10.2822 32.6027 9.8589 33.1457 9.58125C33.6886 9.29904 34.2889 9.16249 34.9422 9.16249C36.112 9.16249 36.9992 9.48111 37.6083 10.1183C38.2175 10.7556 38.5221 11.6796 38.5221 12.8903V13.7051H33.256C33.2737 14.1602 33.3708 14.5289 33.5429 14.8157C33.7151 15.1024 33.9402 15.3072 34.2095 15.4392C34.4831 15.5712 34.7701 15.635 35.079 15.635C35.5558 15.635 35.9177 15.5621 36.1605 15.4165C36.4033 15.2708 36.5754 15.0569 36.6814 14.7792H38.3808C38.2307 15.5849 37.8599 16.1857 37.2729 16.5954C36.6814 17.0005 35.953 17.2053 35.0835 17.2053V17.2007ZM21.6513 16.9914V6.76377H23.5847L27.9723 13.5822V6.76377H29.9057V16.9868H27.9723L23.5847 10.1684V16.9868H21.6513V16.9914ZM65.329 17.1916C64.7199 17.1916 64.2123 17.1279 63.8017 16.9959C63.3912 16.8639 63.0514 16.6864 62.7865 16.4588C62.5217 16.2312 62.323 15.9718 62.1994 15.685C62.0714 15.3937 61.9964 15.0888 61.9699 14.7701H63.8194C63.868 15.0205 64.0092 15.2253 64.252 15.3846C64.4948 15.5439 64.8567 15.6213 65.329 15.6213C65.872 15.6213 66.2604 15.5439 66.4944 15.3892C66.7283 15.2344 66.8431 15.0387 66.8431 14.7974C66.8431 14.6245 66.7901 14.4788 66.6886 14.3696C66.5871 14.2604 66.4017 14.1602 66.1412 14.0783C65.8808 13.9964 65.51 13.9099 65.0333 13.8234C64.6228 13.7642 64.2476 13.6868 63.9033 13.5867C63.5634 13.4866 63.2588 13.3409 62.9984 13.1589C62.7379 12.9768 62.5349 12.7447 62.3892 12.4716C62.2436 12.1939 62.1729 11.8616 62.1729 11.4656C62.1729 11.0423 62.2965 10.6554 62.5437 10.305C62.7909 9.95903 63.1529 9.68138 63.6208 9.47201C64.0931 9.26263 64.6625 9.16249 65.3246 9.16249C66.0441 9.16249 66.618 9.28084 67.0461 9.50842C67.4743 9.74055 67.7965 10.041 68.0128 10.4051C68.2291 10.7738 68.3571 11.1652 68.4057 11.5794H66.5562C66.4811 11.3018 66.3355 11.0878 66.1236 10.9422C65.9073 10.7965 65.6424 10.7237 65.3246 10.7237C64.8567 10.7237 64.5213 10.7965 64.3226 10.9467C64.1196 11.0969 64.0225 11.2836 64.0225 11.502C64.0225 11.7205 64.1328 11.9117 64.3535 12.0391C64.5742 12.1666 65.0245 12.2758 65.7042 12.3714C66.1236 12.4306 66.5164 12.5125 66.874 12.6263C67.2315 12.7355 67.5493 12.8858 67.8186 13.0678C68.0879 13.2499 68.3042 13.482 68.4542 13.7551C68.6087 14.0282 68.6838 14.365 68.6838 14.761C68.6838 15.2344 68.5602 15.6532 68.313 16.0127C68.0658 16.3769 67.6906 16.6591 67.1918 16.8685C66.693 17.0778 66.0706 17.178 65.3202 17.178L65.329 17.1916ZM73.3318 17.1916C72.5903 17.1916 71.9414 17.0187 71.3808 16.6773C70.8202 16.3359 70.3832 15.8625 70.0742 15.2572C69.7608 14.6518 69.6063 13.9599 69.6063 13.1771C69.6063 12.3942 69.7652 11.6841 70.0831 11.0833C70.4009 10.4779 70.8423 10.0091 71.3985 9.67228C71.9591 9.33546 72.6035 9.16704 73.3318 9.16704C74.0602 9.16704 74.7267 9.34001 75.2873 9.68138C75.8479 10.0228 76.2893 10.4961 76.6027 11.1015C76.9161 11.7023 77.075 12.3987 77.075 13.1816C77.075 13.9645 76.9161 14.6655 76.6027 15.2708C76.2849 15.8716 75.8479 16.345 75.2829 16.6864C74.7223 17.0278 74.069 17.1962 73.3363 17.1962L73.3318 17.1916Z" fill="#1C2C39"/>
                          </g>
                          <defs><clipPath id="clip0_8_2"><rect width="88" height="20" fill="white"/></clipPath></defs>
                        </svg>`
      const colorGreen = '#D7F1E7'
      const colorLine = '#5F5F61'
      let docDefinition = {
        pageMargins: [40, 100, 40, 60],
        header: {
          margin: [40, 40, 40, 40],
          stack: [
            {
              alignment: 'justify',
              columns: [
                {
                  width: 250,
                  style: 'header',
                  text: [`SESSION ${this.training.id} REPORT`]
                },
                {
                  width: 130,
                  text: [
                    {
                      style: 'bold',
                      text: `Patient: \n`
                    },
                    {
                      text: `${this.fullNamePatient}`
                    }
                  ]
                },
                {
                  stack: [
                    {
                      text: [
                        {
                          style: 'bold',
                          text: `Sex: `
                        },
                        {
                          text: `${this.gender}\n\n`
                        }
                      ]
                    },
                    {
                      text: [
                        {
                          style: 'bold',
                          text: `Age: `
                        },
                        {
                          text: `${this.age}\n`
                        }
                      ]
                    }
                  ]
                },

                {
                  stack: [
                    {
                      text: [
                        {
                          style: 'bold',
                          text: `Height: `
                        },
                        {
                          text: `${this.height}\n\n`
                        }
                      ]
                    },
                    {
                      text: [
                        {
                          style: 'bold',
                          text: `Weight: `
                        },
                        {
                          text: `${this.weight}\n`
                        }
                      ]
                    }
                  ]
                }
              ]
            },
            `\n`,
            {
              canvas: [
                {
                  lineColor: colorLine,
                  type: 'line',
                  x1: 0,
                  y1: 0,
                  x2: 515,
                  y2: 0,
                  lineWidth: 2
                }
              ]
            },
            `\n`
          ]
        },
        footer: {
          columns: [
            {
              svg: logoNSEng,
              margin: [40, 15, 0, 0]
            },
            {
              text: 'Accordix',
              alignment: 'right',
              style: 'titleIcon',
              margin: [0, 20, 40, 0]
            }
          ]
        },
        info: {
          title: `Session ${this.training.id} report`
        },
        content: [
          {
            text: 'Specified parameters',
            style: 'title'
          },
          {
            margin: [0, 0, 0, 7],
            canvas: [
              {
                lineColor: colorLine,
                type: 'line',
                x1: 0,
                y1: 0,
                x2: 515,
                y2: 0,
                lineWidth: 1.5
              }
            ]
          },
          {
            alignment: 'justify',
            columns: [
              {
                text: [
                  `Doctor: ${this.getSurnameWithInitials(this.training.course.doctor)}\n\n`,
                  `Training type: Standard \n`
                ]
              },
              {
                text: [`Date: ${this.datePlan}\n\n`, `Heart rate limits:  ${this.intervalHR}\n`]
              },
              {
                text: [`Duration: ${this.adjustedDurationStr}\n\n`]
              }
            ]
          },
          `\n`,
          {
            text: `Task: ${this.exercise}`
          },
          '\n\n',
          {
            text: 'Results',
            style: 'title'
          },
          {
            margin: [0, 0, 0, 7],
            canvas: [
              {
                lineColor: colorLine,
                type: 'line',
                x1: 0,
                y1: 0,
                x2: 515,
                y2: 0,
                lineWidth: 1.5
              }
            ]
          },
          {
            alignment: 'justify',
            columns: [
              {
                text: [
                  `Start: ${this.startTime}\n\n`,
                  `Maximum Heart Rate: ${this.maxHeartRate} bpm \n\n`,
                  `Average pace: ${this.averageStepRate}\n\n`
                ]
              },
              {
                text: [
                  `The ending: ${this.endTime} \n\n`,
                  `Average Heart Rate: ${this.averageHeartRate} bpm\n\n`,
                  `Number of steps: ${this.stepCountStr}\n\n`
                ]
              },
              {
                text: [
                  `Duration: ${this.trainingDurationString} \n\n`,
                  `Being in the heart rate zone: ${this.hrInZonePercent}%\n`,
                  `Borg Scale: ${this.borgScaleStr} \n`
                ]
              }
            ]
          },
          '\n',
          {
            text: `Dominant rhythm: ${this.basicRhythm}`
          },
          '\n',
          {
            text: `Diagnosis: ${this.diagnosisStr}`
          },
          '\n',
          {
            alignment: 'justify',
            columns: [
              {
                stack: [
                  {
                    text: 'Patient complaints during and after the session:',
                    style: 'boldMargin'
                  },
                  {
                    canvas: [
                      {
                        lineColor: colorLine,
                        type: 'line',
                        x1: 0,
                        y1: 0,
                        x2: 190,
                        y2: 0,
                        lineWidth: 1.5
                      }
                    ]
                  },
                  `\n`,
                  {
                    text: [
                      {
                        text: `${this.patientCommentStr}`
                      }
                    ]
                  }
                ]
              }
            ]
          },
          '\n'
        ],
        defaultStyle: {
          fontSize: 9
        },
        styles: {
          header: {
            fontSize: 15
          },
          title: {
            fontSize: 10,
            bold: true,
            margin: [0, 0, 0, 3]
          },
          titleIcon: {
            fontSize: 14,
            bold: true
          },
          boldMargin: {
            bold: true,
            margin: [0, 0, 0, 2]
          },
          marginRow: {
            margin: [3, 0, 3, 0]
          },
          bold: {
            bold: true
          },
          italics: {
            fontSize: 12,
            bold: true,
            italics: true
          },
          table: {
            margin: [0, 7, 5, 15]
          },
          tableHeader: {
            bold: true,
            margin: [5, 5, 5, 5]
          },
          marginRowLarge: {
            margin: [5, 8, 5, 0]
          }
        }
      }
      let chart = this.$parent.$parent.$parent.$refs.trainingResult.$refs.trainingChart
        ? this.$parent.$parent.$parent.$refs.trainingResult.$refs.trainingChart.chart
        : null
      if (chart && chart.data.length) {
        chart.series.values.forEach(series => {
          const dashArrayMap = {
            hrSeries: null,
            breathSeries: '8,4',
            stepSeries: '8,4,2,4'
          }

          if (dashArrayMap.hasOwnProperty(series.id)) {
            series.strokeDasharray = dashArrayMap[series.id]
          }
        })
        let imgChart = await chart.exporting.getImage('jpg')
        chart.series.values.forEach(series => {
          series.strokeDasharray = null
        })
        docDefinition.content.push({
          stack: [
            {
              text: 'Data chart',
              style: 'boldMargin'
            },
            {
              canvas: [
                {
                  lineColor: colorLine,
                  type: 'line',
                  x1: 0,
                  y1: 0,
                  x2: 43,
                  y2: 0,
                  lineWidth: 1.5
                }
              ]
            },
            `\n\n`,
            {
              image: imgChart,
              fit: [500, 300]
            },
            `\n\n`
          ],
          unbreakable: true
        })
      }
      if (this.eventsList.length) {
        let res = [
          {
            text: 'Registered cardiac events',
            style: 'title'
          },
          {
            margin: [0, 0, 0, 7],
            canvas: [
              {
                lineColor: colorLine,
                type: 'line',
                x1: 0,
                y1: 0,
                x2: 515,
                y2: 0,
                lineWidth: 1.5
              }
            ]
          },
          {
            alignment: 'justify',
            columns: [
              {
                stack: [
                  {
                    style: 'table',
                    table: {
                      heights: 13,
                      body: this.tableForRegistredEvents.firstColumn
                    },
                    layout: {
                      fillColor: function(rowIndex) {
                        return rowIndex % 2 === 0 ? colorGreen : null
                      },
                      defaultBorder: false
                    }
                  }
                ]
              }
            ]
          }
        ]
        if (this.tableForRegistredEvents.secondColumn.length > 1) {
          res[2].columns.push({
            stack: [
              {
                style: 'table',
                table: {
                  heights: 13,
                  body: this.tableForRegistredEvents.secondColumn
                },
                layout: {
                  fillColor: function(rowIndex) {
                    return rowIndex % 2 === 0 ? colorGreen : null
                  },
                  defaultBorder: false
                }
              }
            ]
          })
        }
        docDefinition.content.push(...res)

        docDefinition.content.push(
          {
            text: 'Conclusion',
            style: 'title'
          },
          {
            margin: [0, 0, 0, 7],
            canvas: [
              {
                lineColor: colorLine,
                type: 'line',
                x1: 0,
                y1: 0,
                x2: 515,
                y2: 0,
                lineWidth: 1.5
              }
            ]
          },
          {
            text: `During the session ${this.conclusionStr}`
          }
        )
      }
      if (window.navigator.pdfViewerEnabled) {
        pdfMake.createPdf(docDefinition).open()
      } else {
        pdfMake.createPdf(docDefinition).download()
      }
    },
    getEventByTime(event, minute) {
      return event.filter(item => {
        let start = this.markers ? minute * 60 + +this.markers.start : minute * 60
        let end = this.markers ? (minute - 1) * 60 + +this.markers.start : minute * 60
        return item.begin <= start && item.begin >= end
      })
    },
    addEventsToList() {
      this.eventsList = this.events.reduce((list, current) => {
        if (!notShow.includes(current.type) && (!current.end || current.begin <= current.end)) {
          let idx = list.findIndex(item => item.abbr === eventsConfig[current.type].abbr)
          if (idx == -1) {
            list.push(eventsConfig[current.type])
            list[list.length - 1].count = 1
            list[list.length - 1].type = current.type
          } else {
            list[idx].count += 1
          }
        }
        return list
      }, [])
    }
  }
}
</script>
